import React, { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import i18n, {
  activateLanguage,
  getLocaleForLanguage,
  getStoredLanguage,
  normalizeLanguage,
  persistLanguage,
  type UiLanguage,
} from '@/shared/i18n/config';

interface SettingsContextType {
  fontScale: number;
  setFontScale: (scale: number) => void;
  language: UiLanguage;
  setLanguage: (language: UiLanguage) => Promise<void>;
  locale: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  theme: ThemeClasses;
  cn: typeof cn;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const DEFAULT_FONT_SCALE = 100;
const MIN_FONT_SCALE = 50;
const MAX_FONT_SCALE = 100;

const validFontScaleOrDefault = (value: unknown): number => {
  const scale = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(scale) && scale >= MIN_FONT_SCALE && scale <= MAX_FONT_SCALE
    ? scale
    : DEFAULT_FONT_SCALE;
};

const subscribeToLanguage = (callback: () => void) => {
  i18n.on('languageChanged', callback);
  return () => {
    i18n.off('languageChanged', callback);
  };
};

const getResolvedLanguage = (): UiLanguage => normalizeLanguage(
  i18n.resolvedLanguage || i18n.language || getStoredLanguage()
);

export const SettingsProvider: React.FC<{ children: React.ReactNode; theme: ThemeClasses }> = ({ children, theme }) => {
  const [fontScale, setStoredFontScale] = React.useState<number>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_FONT_SCALE;
    }

    try {
      const savedScale = window.localStorage.getItem('fontScale');
      return savedScale === null ? DEFAULT_FONT_SCALE : validFontScaleOrDefault(savedScale);
    } catch {
      return DEFAULT_FONT_SCALE;
    }
  });

  const setFontScale = React.useCallback((scale: number) => {
    setStoredFontScale(validFontScaleOrDefault(scale));
  }, []);

  const language: UiLanguage = useSyncExternalStore(
    subscribeToLanguage,
    getResolvedLanguage,
    () => 'en' as UiLanguage
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem('fontScale', fontScale.toString());
    } catch {
      // ignore storage failures
    }

    document.documentElement.style.fontSize = `${fontScale}%`;
  }, [fontScale]);

  useEffect(() => {
    const storedLanguage = getStoredLanguage();
    const resolvedLanguage = getResolvedLanguage();

    if (storedLanguage !== resolvedLanguage) {
      void activateLanguage(storedLanguage);
    }
  }, []);

  useEffect(() => {
    const normalized = normalizeLanguage(language);
    document.documentElement.lang = normalized;
  }, [language]);

  const setLanguage = React.useCallback(async (nextLanguage: UiLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    if (normalized === getResolvedLanguage()) {
      persistLanguage(normalized);
      document.documentElement.lang = normalized;
      return;
    }

    if (await activateLanguage(normalized)) {
      persistLanguage(normalized);
      document.documentElement.lang = normalized;
    }
  }, []);

  const value = useMemo(() => ({
    fontScale,
    setFontScale,
    language,
    setLanguage,
    locale: getLocaleForLanguage(language),
    t: (key: string, options?: Record<string, unknown>) => i18n.t(key, options),
    theme,
    cn,
  }), [fontScale, language, setFontScale, setLanguage, theme]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
